import React, { forwardRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useField } from 'formik';
import classnames from 'classnames';
import { ChevronDown } from 'react-feather';
import { FieldErrorMessage } from '../field-error-message/field-error-message';
import styles from './select-field.module.scss';

export type SelectFieldOption = {
  value: string;
  label: string;
};

export type SelectFieldProps = {
  label: string;
  name: string;
  placeholder?: string;
  options: SelectFieldOption[];
};

type ToggleProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  ({ children, className, ...rest }, ref) => (
    <button
      type={'button'}
      ref={ref}
      className={classnames(styles['toggle'], className)}
      {...rest}
    >
      <span className={styles['toggle-text']}>{children}</span>
      <ChevronDown
        size={16}
        className={styles['chevron']}
        aria-hidden={'true'}
      />
    </button>
  )
);
Toggle.displayName = 'SelectFieldToggle';

export function SelectField({
  name,
  label,
  placeholder,
  options,
}: SelectFieldProps) {
  const [field, meta, helpers] = useField<string>(name);
  const selectedOption = options.find((option) => option.value === field.value);

  return (
    <div>
      <label htmlFor={name} className={styles['label']}>
        {label}
      </label>
      <Dropdown
        onSelect={(eventKey) => helpers.setValue(eventKey ?? '')}
        onToggle={(isOpen) => {
          if (!isOpen) {
            setTimeout(() => helpers.setTouched(true, true), 0);
          }
        }}
      >
        <Dropdown.Toggle
          as={Toggle}
          bsPrefix={'select-field-toggle'}
          id={name}
          className={!selectedOption ? styles['placeholder'] : undefined}
        >
          {selectedOption?.label ?? placeholder ?? ''}
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles['menu']}>
          {options.map((option) => (
            <Dropdown.Item
              key={option.value}
              eventKey={option.value}
              role={'option'}
              active={option.value === field.value}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
      <FieldErrorMessage meta={meta} />
    </div>
  );
}

export default SelectField;
